<script setup lang="ts">
import { computed } from 'vue'
import { NButton, NDatePicker, NAlert } from 'naive-ui'
import { usePursuitFiltersUrl } from '@/composables/usePursuitFiltersUrl'

const emit = defineEmits<{ refresh: [] }>()

const { filters, update } = usePursuitFiltersUrl()

const DAY_MS = 24 * 3600 * 1000

const PRESETS = [
  { key: '1h', label: '1ч', hours: 1 },
  { key: '6h', label: '6ч', hours: 6 },
  { key: '24h', label: '24ч', hours: 24 },
  { key: '7d', label: '7д', hours: 168 },
  { key: '30d', label: '30д', hours: 720 },
] as const

function applyPreset(hours: number) {
  const until = new Date()
  const since = new Date(until.getTime() - hours * 3600 * 1000)
  update({ since, until, page: 1 })
}

const rangeValue = computed<[number, number]>(() => [
  filters.value.since.getTime(),
  filters.value.until.getTime(),
])

function onRangeChange(value: [number, number] | null) {
  if (!value) return
  update({ since: new Date(value[0]), until: new Date(value[1]), page: 1 })
}

const isInvalidRange = computed(
  () => filters.value.since.getTime() >= filters.value.until.getTime(),
)

// Активный пресет = тот, чья длительность совпадает с текущим окном (since..until).
// Толеранс 0.05ч (~3 мин) гасит дрейф volatile-дефолтов. Кастомное окно → null.
const activePresetKey = computed(() => {
  const spanHours =
    (filters.value.until.getTime() - filters.value.since.getTime()) / 3_600_000
  return PRESETS.find((p) => Math.abs(spanHours - p.hours) <= 0.05)?.key ?? null
})

function onRefresh() {
  if (!isInvalidRange.value) emit('refresh')
}

// Сброс к дефолтам (совпадают с usePursuitFiltersUrl): окно 24ч, стр. 1, 25/стр., Мин 1.
function onReset() {
  update({
    since: new Date(Date.now() - DAY_MS),
    until: new Date(),
    page: 1,
    page_size: 25,
    min_appearances: 1,
  })
}
</script>

<template>
  <div class="filters">
    <div class="filters-row top-row">
      <div class="left-group">
        <n-button
          v-for="p in PRESETS"
          :key="p.key"
          :data-preset="p.key"
          :class="{ 'is-active': p.key === activePresetKey }"
          size="small"
          :type="p.key === activePresetKey ? 'primary' : 'default'"
          :quaternary="p.key !== activePresetKey"
          @click="applyPreset(p.hours)"
        >
          {{ p.label }}
        </n-button>
      </div>
      <div class="right-group">
        <n-date-picker
          :value="rangeValue"
          type="datetimerange"
          format="yyyy-MM-dd HH:mm"
          :time-picker-props="{ format: 'HH:mm' }"
          clearable
          @update:value="onRangeChange"
        />
        <n-button
          size="small"
          data-action="reset"
          @click="onReset"
        >
          ↺ Сбросить
        </n-button>
        <n-button
          type="primary"
          size="small"
          data-action="refresh"
          :disabled="isInvalidRange"
          @click="onRefresh"
        >
          ⟳ Обновить
        </n-button>
      </div>
    </div>
    <n-alert v-if="isInvalidRange" type="warning" style="margin-top: 8px">
      Некорректный диапазон: начало позже окончания.
    </n-alert>
  </div>
</template>

<style scoped>
.filters-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.top-row {
  justify-content: space-between;
}
.left-group,
.right-group {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
/* Активный пресет — выделен заливкой акцентом (тип primary даёт фон/цвет). */
.is-active { font-weight: 600; }
</style>
